import React, { useState, useEffect } from "react";
import { Input } from "components/input";
import { Button } from "components/button";
import { useMutation } from "react-query";
import Axios from "axios";
import { Formik, Form } from "formik";
import { useSnackbarContext } from "components/snackbar";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import Spinner from "components/spinner";
import Field from "components/field";
import { RxOpenInNewWindow } from "react-icons/rx";
import { Link } from "react-router-dom";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa";
import { getContrastTextColorRGB } from "utils/getContrastTextColor";
import { useAuthContext } from "contexts/auth";
import convertCVD  from "utils/convertCVD";

const Accessibility = (props) => {
  const { t } = useTranslation();
  const tBase = "views.profile.components.accessibility";
  const tr = (key) => t(`${tBase}.${key}`);
  const { showSnackbar } = useSnackbarContext();
  const { company_id: companyId } = useAuthContext();

  const { userInfo, refetchUserInfo } = props;

  const [showPreview, setShowPreview] = useState(false);
  const [statusArray, setStatusArray] = useState([]);
  const [colorScheme, setColorScheme] = useState("");
  const [selectedCvdType, setSelectedCvdType] = useState(userInfo?.cvd_type || "");

  const updateUser = useMutation(
    (values) =>
      Axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/users/${userInfo.user_id}`,
        values
      ),
    {
      onSuccess: () => {
        showSnackbar(tr("User info updated!"));
      },
      onError: (e) => {
        console.error("Error updating user info", e);
        showSnackbar(tr("Error updating user info"))
      },
    }
  );

  const getStatus = async () => {
    const res = await Axios.get( 
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/status/custom`, 
    );

    const noStatJob = ["Converted to Job"];
    const noStatProj = ["Won", "Lost"];
    const noStatQuote1 = ["Approved"];
    const noStatQuote2 = ["Denied"];

    const groupStatuses = res?.data?.statuses.reduce((acc, status) => {
      if (status.type === "job") return acc; // no job
      if ((companyId !== 1 && companyId !== 13) && noStatJob.includes(status.label)) return acc; // no converted to job except for MS && WeatherGuard
      if ((companyId !== 1 && companyId !== 13) && noStatQuote1.includes(status.label)) return acc; // no converted to job except for MS && WeatherGuard
      if (companyId !== 1 && noStatQuote2.includes(status.label)) return acc; // no converted to job except for MS 
      if (companyId !== 1 && status.type === "project" && noStatProj.includes(status.label)) return acc; // no won or lost for projects

      if (!acc[status.type]) acc[status.type] = [];
      acc[status.type].push(status);
      return acc;
    }, {});

    setStatusArray(groupStatuses);
    setColorScheme(res?.data?.colorScheme);
  };

  const getTagStyle = (type, color) => {
    let style;

    if (type === "appointment") {
      style = {
        backgroundColor: `rgb(${color})`,
        color: getContrastTextColorRGB(color)
      }
    } else {
      if (color === "255,255,255") {
        style = {
          backgroundColor: "transparent",
          color: "black",
          border: "1px solid #ccc",
          fontWeight: 800,
        };
      } else {
        style = {
          backgroundColor: `rgba(${color}, 0.15)`,
          color: `rgb(${color})`,
          fontWeight: 800,
        }
      }
    }

    return style;
  }

  useEffect(() => {
    getStatus();
  }, []);

  const initialValues = {
    cvd_type: userInfo?.cvd_type || "",
  };

  const validationSchema = Yup.object({
    cvd_type: Yup.string(),
  });

  const CVD_TYPES = [
    { value: "", label: tr("None"), color: "#FFFFFF" },
    { value: "deuteranopia", label: "Deuteranopia", color: "#A3A3A3" }, 
    { value: "protanopia", label: "Protanopia", color: "#A67C52" }, 
    { value: "tritanopia", label: "Tritanopia", color: "#00A4A6" }, 
  ];

  const CustomOption = ({ data, innerRef, innerProps }) => {
    return (
      <div
        ref={innerRef}
        {...innerProps}
        style={{
          display: "flex",
          alignItems: "center",
          padding: "10px",
          cursor: "pointer",
          backgroundColor: innerProps.isFocused ? "#f0f0f0" : "white",
        }}
      >
        <div
          style={{
            width: "20px",
            height: "20px",
            backgroundColor: data.color,
            border: data.value === "" ? "1px solid #ccc" : "none",
            marginRight: "10px",
            borderRadius: "3px",
          }}
        ></div>
        <span>{data.label}</span>
      </div>
    );
  };

  const CustomSingleValue = ({ data }) => {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: "20px",
            height: "20px",
            backgroundColor: data.color,
            border: data.value === "" ? "1px solid #ccc" : "none",
            marginRight: "10px",
            borderRadius: "3px",
          }}
        ></div>
        <span>{data.label}</span>
      </div>
    );
  };

  return (
    <div className="flex flex-wrap gap-5">
      <div className="flex-1 max-w-[500px] min-w-[300px] flex flex-col">
        {userInfo ? (
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values, { resetForm }) => {
              // updateUser.mutate(values);
              // resetForm(values);
              updateUser.mutate(values, {
                onSuccess: () => {
                  refetchUserInfo();
                  setTimeout(() => {
                    resetForm(values);
                  }, 100);
                },
              });
            }}
          >
            {({
              touched,
              errors,
              dirty,
              isValid,
              values,
              handleBlur,
              handleChange,
            }) => {
              return (
                <>
                  <div className="mb-2 flex items-center justify-between">
                    <div>{tr("Color Vision Difference Type")}</div>
                      <div className="flex items-center space-x-1">
                        <button 
                          onClick={() => setShowPreview(!showPreview)}
                          className="flex items-center space-x-1 text-blue-500 underline"
                        >
                          <div>{tr("Preview")}</div>
                          {showPreview ? <FaRegEye size={18} /> : <FaRegEyeSlash size={18} />}
                        </button>
                      </div>
                  </div>
                  <Form>
                    <Field
                      label=""
                      type="select"
                      name="cvd_type"
                      error={touched.cvd_type && errors.cvd_type}
                      value={CVD_TYPES.find((option) => option.value === values.cvd_type) || null}
                      onChange={(selectedOption) => {
                        const newCvdType = selectedOption?.value || "";
                        setSelectedCvdType(newCvdType); 
                        handleChange({
                          target: { name: "cvd_type", value: newCvdType },
                        });
                      }}
                      onBlur={handleBlur}
                      options={CVD_TYPES}
                      components={{
                        Option: CustomOption, 
                        SingleValue: CustomSingleValue, 
                      }}
                    />
                    <Button
                      primary
                      type="submit"
                      disabled={!dirty || !isValid}
                      className="mt-4"
                    >
                      {tr("Submit")}
                    </Button>
                  </Form>
                </>
              );
            }}
          </Formik>
        ) : (
          <Spinner size={26} color="#ff5200" />
        )}
      </div>
      {/* Preview */}
      <div
        className="flex-1 min-w-[300px] flex flex-col">
        {showPreview && (
          <div>
            {Object.entries(statusArray || {}).map(([type, statuses]) => (
              <div key={type} className="mb-6 border border-gray-200 rounded-lg p-4 shadow-md space-y-2">
                <h2 className="text-xl font-semibold capitalize mb-4">
                  {type === "service_plan" ? tr("Service Plans") : tr(type)}
                </h2>
                <div className="flex flex-wrap gap-2">
                  {statuses.map((status) => {
                    const { id, label, color_v2, color, custom_color } = status;
                    const currentColor = custom_color
                      ? custom_color
                      : colorScheme === "legacy"
                      ? color
                      : color_v2;

                    const convertedColor = convertCVD(currentColor, selectedCvdType);
      
                    return (
                      <div 
                        key={id}
                        className="text-white px-3 py-1 rounded-md text-sm font-medium border" 
                        style={getTagStyle(type, convertedColor)}
                      >
                        {tr(label)}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Accessibility;